import { Budgets, Plan, Strategy } from './model';
export type Calculation = { errors:string[]; entryErrors:string[]; riskErrors:string[]; notional?:number; loss?:number; dayLoss?:number; swingLoss?:number; profit?:number; rr?:number; maxShares?:number; allocation?:number; scenarioLoss?:number };
export function calculate(p:Plan,b:Budgets,mode:Strategy):Calculation {
  const entryErrors:string[]=[],riskErrors:string[]=[];
  const number=(raw:string,label:string,errors:string[],zero=false) => {
    const n=raw.trim()===''?NaN:Number(raw);
    if(!Number.isFinite(n)||(zero?n<0:n<=0)) errors.push(`${label} must be ${zero?'zero or positive':'positive'} and finite.`);
    return n;
  };
  const entry=number(p.entry,'Entry price',entryErrors),shares=number(p.shares,'Shares',riskErrors);
  const cash=number(b.cash,'Available trading cash',riskErrors),fees=number(p.fees,'Round-trip fees',riskErrors,true),slip=number(p.slippage,'Slippage per share per side',riskErrors,true);
  if(!Number.isInteger(shares))riskErrors.push('Use whole shares for this calculator.');
  const result:Calculation={errors:[],entryErrors,riskErrors};
  const investment=mode==='investment',day=mode==='day'||mode==='both',swing=mode==='short'||mode==='both';
  let loss=0,profit=0,allTargets=true;
  if(investment){
    const cap=number(p.allocationPercent,'Allocation limit',riskErrors);
    if(cap>100)riskErrors.push('Allocation limit cannot exceed 100%.');
    if(!p.invalidation)entryErrors.push('Choose a thesis invalidation condition.');
    if(!p.investmentExit)entryErrors.push('Choose an investment exit policy.');
    if(p.adversePrice.trim()){
      const adverse=number(p.adversePrice,'Adverse scenario price',riskErrors,true);
      if(adverse>=entry)riskErrors.push('Adverse scenario price must be below entry.');
      result.scenarioLoss=shares*(entry-adverse)+2*shares*slip+fees;
    }
    result.allocation=shares*entry/cash*100;
    if(result.allocation>cap+1e-9)riskErrors.push('Position exceeds your allocation limit.');
    result.maxShares=Math.max(0,Math.min(Math.floor((cash-fees)/(entry+slip)),Math.floor(cash*cap/100/entry)));
  }else{
    const budget=number(b.tradeLoss,'Maximum planned loss per trade',riskErrors);
    if(budget>cash)riskErrors.push('Trade loss limit exceeds trading cash.');
    const stop=number(p.stop,'Stop price',entryErrors);
    if(stop>=entry)entryErrors.push('Stop must be below entry for a long position.');
    let dayShares=mode==='day'?shares:0;
    if(mode==='both'){
      dayShares=number(p.dayShares,'Day shares',entryErrors);
      if(!Number.isInteger(dayShares)||dayShares>=shares)entryErrors.push('Both needs whole day shares greater than zero and less than total shares.');
    }
    const overnightShares=shares-dayShares;
    let swingStop=stop;
    if(mode==='both'&&p.swingStop.trim()){
      swingStop=number(p.swingStop,'Swing stop',entryErrors);
      if(swingStop>=entry)entryErrors.push('Swing stop must be below entry.');
    }
    const portion=(count:number,partStop:number,policy:string,trail:string,label:string)=>{
      if(count<=0)return 0;
      const partFees=fees*count/shares;
      if(policy==='target'){
        const target=number(p.target,'Target price',entryErrors);
        if(target<=entry)entryErrors.push('Target must be above entry.');
        profit+=count*(target-entry-2*slip)-partFees;
      }else{
        allTargets=false;
        if(policy==='trailing'){
          const distance=number(trail,`${label} trailing distance`,entryErrors);
          if(distance>=entry)entryErrors.push(`${label} trailing distance must be below entry price.`);
        }
      }
      return count*(entry-partStop+2*slip)+partFees;
    };
    result.dayLoss=portion(dayShares,stop,p.dayExit,p.dayTrail,'Day');
    result.swingLoss=portion(overnightShares,swingStop,p.swingExit,p.swingTrail,'Swing');
    loss=result.dayLoss+result.swingLoss;
    result.loss=loss;
    if(loss>budget+1e-9)riskErrors.push('Planned loss exceeds your per-trade limit.');
    if(day){
      const limit=number(b.dayLoss,'Maximum planned loss per day',riskErrors);
      if(limit>cash)riskErrors.push('Daily loss limit exceeds trading cash.');
      if(result.dayLoss>limit+1e-9)riskErrors.push('Day portion exceeds your daily loss policy.');
      if(!p.sameDayExit)entryErrors.push('Acknowledge the same-day exit for day-trade shares.');
    }
    if(swing){
      const sessions=number(p.swingSessions,'Short-swing holding sessions',entryErrors);
      if(!Number.isInteger(sessions)||sessions<2||sessions>10)entryErrors.push('Short swing must be 2–10 trading sessions.');
    }
    if(mode==='medium') {
      const sessions=number(p.swingSessions,'Medium-swing holding sessions',entryErrors);
      if(!Number.isInteger(sessions)||sessions<=10||sessions>252)entryErrors.push('Medium swing must be 11–252 trading sessions (up to about one year).');
    }
    if(allTargets){result.profit=profit;result.rr=profit/loss;}
    const perShare=(dayShares*(entry-stop+2*slip)+overnightShares*(entry-swingStop+2*slip))/shares;
    result.maxShares=Math.max(0,Math.min(Math.floor((cash-fees)/(entry+slip)),Math.floor((budget-fees)/perShare)));
    if(day&&Number(b.dayLoss)>0&&dayShares>0){
      const fraction=dayShares/shares;
      const dailyMax=Math.floor((Number(b.dayLoss)-fees*fraction)/((entry-stop+2*slip)*fraction));
      result.maxShares=Math.max(0,Math.min(result.maxShares,dailyMax));
    }
  }
  result.notional=shares*entry;
  if(shares*(entry+slip)+fees>cash+1e-9)riskErrors.push('Position and estimated costs exceed available trading cash.');
  if(!entryErrors.length&&!riskErrors.length&&Object.values(result).some(v=>typeof v==='number'&&!Number.isFinite(v)))riskErrors.push('Values are too large or too small to calculate safely.');
  result.errors=[...new Set([...entryErrors,...riskErrors])];
  if(result.errors.length){
    // Never display NaN/infinite estimates for partially completed inputs.
    for(const key of ['notional','loss','dayLoss','swingLoss','profit','rr','maxShares','allocation','scenarioLoss'] as const){if(!Number.isFinite(result[key]))delete result[key];}
  }
  return result;
}
